import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Send, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import { BroadcastStatusChip } from '@/pages/admin/AdminBroadcasts';
import { Button } from '@/components/ui/Button';
import { adminBroadcastsService } from '@/services/admin-broadcasts.service';
import { adminEmailTemplatesService } from '@/services/admin-email-templates.service';
import { ApiError } from '@/services/http';
import type { BroadcastDetailView, EmailTemplateView } from '@/types/api';

export function AdminBroadcastDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [broadcast, setBroadcast] = useState<BroadcastDetailView | null>(null);
  const [template, setTemplate] = useState<EmailTemplateView | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [testEmail, setTestEmail] = useState('');
  const [spam, setSpam] = useState<{ score: number | null; report: string | null } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const reload = async () => {
    if (!id) return;
    try {
      const detail = await adminBroadcastsService.getById(id);
      setBroadcast(detail);
      if (detail.templateId) {
        adminEmailTemplatesService
          .getById(detail.templateId)
          .then(setTemplate)
          .catch(() => setTemplate(null));
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not load broadcast.');
      navigate('/admin/subscribers/broadcasts', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [id]);

  const handleSpamCheck = async () => {
    if (!id) return;
    setBusy(true);
    try {
      setSpam(await adminBroadcastsService.spamCheck(id));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Spam check failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    if (!id || !testEmail.trim()) return;
    setBusy(true);
    try {
      await adminBroadcastsService.test(id, testEmail.trim());
      toast.success(`Test sent to ${testEmail.trim()}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Test send failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!id) return;
    setConfirmOpen(false);
    setConfirmText('');
    setBusy(true);
    try {
      const updated = await adminBroadcastsService.send(id);
      setBroadcast(updated);
      if (updated.status === 'failed') {
        toast.error(updated.error ?? 'Send failed.');
      } else {
        toast.success('Broadcast queued for sending.');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Send failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    setBusy(true);
    try {
      setBroadcast(await adminBroadcastsService.cancel(id));
      toast.success('Broadcast canceled.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not cancel.');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !broadcast) {
    return (
      <AppShell mode="parent">
        <div className="py-16 text-center text-or-slate-500">Loading…</div>
      </AppShell>
    );
  }

  const isDraft = broadcast.status === 'draft';
  const inFlight = broadcast.status === 'queued' || broadcast.status === 'sending';
  const cancellable = isDraft || inFlight;
  const stats = broadcast.stats;

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/subscribers/broadcasts')}
              className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-600 hover:bg-or-slate-50"
              aria-label="Back to broadcasts"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <div className="or-kicker text-or-orange-600">Broadcast</div>
                <BroadcastStatusChip status={broadcast.status} />
              </div>
              <h1 className="mt-0.5 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
                {broadcast.subject}
              </h1>
              <p className="text-[13px] text-or-slate-600">
                To <span className="font-semibold">{broadcast.listName ?? '—'}</span>
                {broadcast.recipientCount > 0
                  ? ` · ${broadcast.recipientCount.toLocaleString()} recipients`
                  : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => void reload()} disabled={busy}>
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              Refresh
            </Button>
            {cancellable ? (
              <Button variant="danger" size="sm" onClick={handleCancel} disabled={busy}>
                Cancel
              </Button>
            ) : null}
          </div>
        </div>

        {broadcast.error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {broadcast.error}
          </div>
        ) : null}

        {/* Stats (after a send) */}
        {!isDraft ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
            <Stat label="Recipients" value={broadcast.recipientCount} />
            <Stat label="Sent" value={stats.sent} />
            <Stat label="Delivered" value={stats.delivered} />
            <Stat label="Opened" value={stats.opened} />
            <Stat label="Clicked" value={stats.clicked} />
            <Stat label="Bounced" value={stats.bounced} />
            <Stat label="Complained" value={stats.complained} />
            <Stat label="Unsubscribed" value={stats.unsubscribed} />
            <Stat label="Failed" value={stats.failed} />
            <Stat label="Skipped" value={stats.skipped} />
            <Stat label="Pending" value={stats.pending} />
          </div>
        ) : null}

        <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
          {/* Preview */}
          <div className="flex flex-col gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
              Preview {template ? '' : '(template unavailable — snapshot will be used)'}
            </div>
            {template?.format === 'html' ? (
              <iframe
                title="Broadcast preview"
                srcDoc={template.htmlBody ?? ''}
                sandbox=""
                className="h-[460px] w-full rounded-xl border border-or-slate-200 bg-white"
              />
            ) : template?.format === 'text' ? (
              <pre className="h-[460px] w-full overflow-auto whitespace-pre-wrap rounded-xl border border-or-slate-200 bg-white p-4 font-mono text-[13px] text-or-slate-800">
                {template.textBody}
              </pre>
            ) : (
              <div className="rounded-xl border border-or-slate-200 bg-white p-6 text-[13px] text-or-slate-500">
                No preview available.
              </div>
            )}
          </div>

          {/* Actions */}
          <aside className="flex flex-col gap-4">
            {isDraft ? (
              <>
                <div className="flex flex-col gap-2 rounded-2xl border border-or-slate-200 bg-white p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
                    Send a test
                  </div>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleTest}
                    disabled={busy || !testEmail.trim()}
                  >
                    Send test
                  </Button>
                </div>

                <div className="flex flex-col gap-2 rounded-2xl border border-or-slate-200 bg-white p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
                    Spam check
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleSpamCheck} disabled={busy}>
                    <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                    Run Spam Check
                  </Button>
                  {spam ? (
                    <p className="text-[12px] text-or-slate-600">
                      {spam.score === null
                        ? 'Spam Check unavailable right now.'
                        : `SpamAssassin score: ${spam.score} (lower is better; aim for < 5).`}
                    </p>
                  ) : null}
                </div>

                <Button variant="primary" size="md" onClick={() => setConfirmOpen(true)} disabled={busy}>
                  <Send className="h-4 w-4" aria-hidden />
                  Send broadcast
                </Button>
                <p className="text-[11px] text-or-slate-500">
                  Sends to every active member of {broadcast.listName ?? 'the list'} (honouring
                  account marketing opt-outs). This can't be undone.
                </p>
              </>
            ) : inFlight ? (
              <div className="rounded-2xl border border-or-slate-200 bg-white p-4 text-[13px] text-or-slate-600">
                Sending in progress — {stats.sent}/{broadcast.recipientCount} sent. Hit Refresh to
                update.
              </div>
            ) : (
              <div className="rounded-2xl border border-or-slate-200 bg-white p-4 text-[13px] text-or-slate-600">
                This broadcast is <span className="font-semibold">{broadcast.status}</span>.
                {broadcast.completedAt
                  ? ` Completed ${new Date(broadcast.completedAt).toLocaleString()}.`
                  : ''}
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* Send confirmation — type SEND */}
      {confirmOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-or-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setConfirmOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-or-slate-200 bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-display text-[18px] font-bold text-or-slate-900">
              Send this broadcast?
            </h2>
            <p className="mt-1 text-[13px] text-or-slate-600">
              "{broadcast.subject}" will be emailed to every active member of{' '}
              <span className="font-semibold">{broadcast.listName ?? 'the list'}</span>. This can't be
              undone. Type <span className="font-mono font-semibold">SEND</span> to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="SEND"
              className="mt-4 h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSend}
                disabled={confirmText !== 'SEND' || busy}
              >
                Send now
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </AppShell>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-or-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
        {label}
      </div>
      <div className="mt-0.5 font-display text-[20px] font-bold text-or-slate-900">
        {value.toLocaleString()}
      </div>
    </div>
  );
}
